import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FileDown, FolderOpen, Layers, Loader2, Upload, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  useGetUploadBatch,
  useListDocumentTypes,
  useListUploadBatches,
} from "@/api/client-document";
import type { DocumentTypeValue } from "@/api/model";
import { TypeGuide } from "@/components/sites/document-upload/type-guide";
import { DocumentTypeList } from "@/components/sites/document-upload/document-type-list";
import {
  ACCEPTED_FILE_TYPES,
  DocumentDropPlate,
  MAX_FILES,
} from "@/components/sites/document-upload/drop-plate";
import { PreviousDocumentUploads } from "@/components/sites/document-upload/previous-uploads";
import {
  StagedFileList,
  stagedFileKey,
} from "@/components/sites/document-upload/staged-file-list";
import { UploadComplete } from "@/components/sites/document-upload/upload-complete";
import { UploadProgress } from "@/components/sites/document-upload/upload-progress";
import {
  FILE_STATUS_FROM_API,
  type UploadFileState,
  useDocumentUpload,
} from "@/components/sites/document-upload/use-document-upload";
import { Button } from "@/components/ui/button";
import {
  FloatingActionBar,
  FloatingActionBarDivider,
} from "@/components/ui/floating-action-bar";
import { useRegisterOnboardingReset } from "@/components/sites/onboarding-reset";
import { useSiteStore } from "@/stores/site-store";

/**
 * How each file's outcome reads in the downloaded report.
 *
 * "Already held" is its own result rather than a kind of upload: nothing was
 * transferred, and nothing went wrong either — the platform already had the
 * certificate and linked this site to it. Calling that "Uploaded" would put a
 * transfer in the record that never happened.
 */
const FILE_RESULT: Partial<Record<UploadFileState["status"], string>> = {
  uploaded: "Uploaded",
  reused: "Already held",
};

export const Route = createFileRoute("/dashboard/onboarding/document-upload")({
  component: DocumentUploadPage,
  staticData: {
    title: "Bulk upload documents",
  },
});

function DocumentUploadPage() {
  const { selectedSiteId } = useSiteStore();
  const navigate = useNavigate();

  const { data: typesData, isLoading: typesLoading } = useListDocumentTypes(
    { siteId: selectedSiteId ?? "" },
    { query: { enabled: !!selectedSiteId } },
  );
  const types = typesData?.types;

  const { data: batchesData } = useListUploadBatches(
    { siteId: selectedSiteId ?? "" },
    { query: { enabled: !!selectedSiteId } },
  );

  // No type is pre-selected. Every file in a drop is filed as one type, so
  // landing on a default invites 500 documents going in under whatever happened
  // to be first in the list.
  const [selectedType, setSelectedType] = useState<DocumentTypeValue | null>(
    null,
  );
  const [files, setFiles] = useState<File[]>([]);
  /** Set when a past upload is opened from the history list. */
  const [viewedBatchId, setViewedBatchId] = useState<string | null>(null);

  const upload = useDocumentUpload(selectedSiteId ?? null);
  const isUploading =
    upload.phase === "preparing" || upload.phase === "uploading";

  // Reopening a past batch reads its files back from the server, so the
  // receipt is the same component whether the upload just finished or
  // happened last week.
  const { data: viewedBatch, isLoading: viewedBatchLoading } =
    useGetUploadBatch(viewedBatchId ?? "", {
      query: { enabled: !!viewedBatchId },
    });

  const historyFiles = useMemo<UploadFileState[]>(
    () =>
      (viewedBatch?.files ?? []).map((file) => ({
        key: file.id,
        filename: file.filename,
        sizeBytes: file.size_bytes,
        status: FILE_STATUS_FROM_API[file.status] ?? "failed",
        failureReason: file.failure_reason ?? null,
      })),
    [viewedBatch],
  );

  const viewingHistory = !!viewedBatchId;
  const showHistoryReceipt = viewingHistory && !!viewedBatch;
  const isFinished =
    showHistoryReceipt ||
    upload.phase === "complete" ||
    upload.phase === "cancelled" ||
    upload.phase === "error";
  const showReceipt = showHistoryReceipt || upload.phase === "complete";

  const startOver = useCallback(() => {
    upload.reset();
    setFiles([]);
    setViewedBatchId(null);
  }, [upload]);

  // Whichever set of files is on screen is what the report describes.
  const reportFiles = showHistoryReceipt ? historyFiles : upload.files;
  const reportType = showHistoryReceipt
    ? (viewedBatch?.batch.document_type ?? null)
    : selectedType;

  const downloadReport = useCallback(() => {
    const rows = [
      ["File name", "Size (bytes)", "Result", "Detail"],
      ...reportFiles.map((f) => [
        f.filename,
        String(f.sizeBytes),
        FILE_RESULT[f.status] ?? "Not uploaded",
        f.status === "reused"
          ? "Already held — linked to this site rather than uploaded again"
          : (f.failureReason ?? ""),
      ]),
    ];
    const csv = rows
      .map((row) =>
        // replaceAll is ES2021; the build targets es2020/safari15.
        row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `document-upload-${reportType ?? "report"}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [reportFiles, reportType]);

  // The picker only makes sense before an upload starts; afterwards the page
  // is a progress read-out, or a receipt being read back.
  const showPicker = upload.phase === "idle" && !viewingHistory;

  // Clicking "Documents" in the step rail while a receipt is on screen returns
  // to the picker. Not offered mid-upload — that click would abandon a
  // transfer in flight.
  useRegisterOnboardingReset(isFinished ? startOver : null);

  const labelFor = useCallback(
    (type: string | null) =>
      type ? (types?.find((t) => t.type === type)?.label ?? type) : null,
    [types],
  );
  const selectedLabel = useMemo(
    () => labelFor(selectedType),
    [labelFor, selectedType],
  );
  const selectedTypeDTO = useMemo(
    () => types?.find((t) => t.type === selectedType),
    [types, selectedType],
  );
  const receiptLabel = showHistoryReceipt
    ? labelFor(viewedBatch?.batch.document_type ?? null)
    : selectedLabel;

  const onDrop = useCallback((accepted: File[]) => {
    setFiles((current) => {
      // Dropping the same batch twice is easy to do and never intentional, so
      // identical files are folded together rather than staged again.
      const seen = new Set(current.map(stagedFileKey));
      const added = accepted.filter((file) => !seen.has(stagedFileKey(file)));
      return [...current, ...added].slice(0, MAX_FILES);
    });
  }, []);

  const removeFile = useCallback((key: string) => {
    setFiles((current) =>
      current.filter((file) => stagedFileKey(file) !== key),
    );
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxFiles: MAX_FILES,
    // The plate is inert until a type is chosen — there is nowhere to file a
    // dropped document before then.
    disabled: !selectedType || !showPicker,
    noClick: !selectedType || !showPicker,
  });

  return (
    <>
      <div className="flex flex-col gap-6 md:gap-8">
        {/* The asset and its onboarding status are in the layout header; this
            only has to introduce the screen. */}
        <header className="min-w-0">
          <h2 className="text-lg font-semibold tracking-[-0.005em]">
            Bulk upload documents
          </h2>
          <p className="text-muted-foreground mt-0.5 text-sm">
            One document type per upload, so every file in the drop is filed the
            same way.
          </p>
        </header>

        <input {...getInputProps()} />

        {showPicker ? (
          <>
            <div className="grid gap-6 @3xl/main:grid-cols-[minmax(0,19rem)_minmax(0,1fr)]">
              <DocumentTypeList
                types={types}
                groups={typesData?.groups}
                isLoading={typesLoading}
                selected={selectedType}
                onSelect={(type) => {
                  setSelectedType(type);
                }}
              />

              <div className="flex min-w-0 flex-col gap-4">
                {/* Above the plate: it names what to drop for the chosen type,
                    so it is read before anything is dragged in. */}
                {selectedTypeDTO && <TypeGuide docType={selectedTypeDTO} />}

                {/* The plate stretches to fill the column beside the type list
                    rather than leaving it empty below — the list is fourteen rows
                    tall, and a drop target is easier to hit the bigger it is. */}
                <DocumentDropPlate
                  rootProps={getRootProps()}
                  isDragActive={isDragActive}
                  typeLabel={selectedLabel}
                  files={files}
                />

                <StagedFileList files={files} onRemove={removeFile} />
              </div>
            </div>

            <PreviousDocumentUploads
              batches={batchesData?.batches}
              types={types}
              onSelect={setViewedBatchId}
            />
          </>
        ) : viewedBatchLoading ? (
          <div className="text-muted-foreground flex min-h-[19rem] items-center justify-center gap-2 rounded-[10px] border text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Opening that upload…
          </div>
        ) : showReceipt ? (
          <UploadComplete
            typeLabel={receiptLabel}
            files={showHistoryReceipt ? historyFiles : upload.files}
            batch={
              showHistoryReceipt ? (viewedBatch?.batch ?? null) : upload.batch
            }
            types={types}
            onUploadAnother={startOver}
            onReviewLinks={() =>
              navigate({ to: "/dashboard/onboarding/linking" })
            }
          />
        ) : (
          <UploadProgress
            typeLabel={selectedLabel}
            files={upload.files}
            uploaded={upload.uploaded}
            failed={upload.failed}
            inFlight={upload.inFlight}
            queued={upload.queued}
            reused={upload.reused}
            finished={isFinished}
            cancelled={upload.phase === "cancelled"}
          />
        )}

        {upload.errorMessage && (
          <p className="text-destructive text-sm">{upload.errorMessage}</p>
        )}
      </div>

      <FloatingActionBar visible>
        {showPicker && (
          <>
            {/* No Cancel: leaving this screen is navigation, and the step rail
                above already offers every way out. */}
            <Button
              variant="ghost"
              className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground gap-2"
              disabled={!selectedType}
              onClick={open}
            >
              <FolderOpen className="size-4" aria-hidden />
              {files.length > 0 ? "Add more files" : "Select files"}
            </Button>
            {files.length > 0 && selectedType && (
              <Button
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 gap-2"
                onClick={() => {
                  void upload.start(files, selectedType);
                }}
              >
                <Upload className="size-4" aria-hidden />
                Upload {files.length.toLocaleString()}{" "}
                {files.length === 1 ? "file" : "files"}
              </Button>
            )}
          </>
        )}

        {isUploading && (
          <Button
            variant="ghost"
            className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground gap-2"
            onClick={() => {
              void upload.cancel();
            }}
          >
            <X className="size-4" aria-hidden />
            Cancel upload
          </Button>
        )}

        {isFinished && (
          <>
            <Button
              variant="ghost"
              className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground gap-2"
              onClick={downloadReport}
            >
              <FileDown className="size-4" aria-hidden />
              Download report
            </Button>
            <FloatingActionBarDivider />
            {/* Both actions act on this page. Where to go next is offered in
                the body, not the bar. */}
            <Button
              className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 gap-2"
              onClick={startOver}
            >
              <Layers className="size-4" aria-hidden />
              Upload another type
            </Button>
          </>
        )}
      </FloatingActionBar>
    </>
  );
}
